import { MatDialogRef } from '@angular/material/dialog';
import * as CKEditor from 'ckeditor5';
import { lastValueFrom } from 'rxjs';
import { AifDialogComponent } from './aif-dialog/aif-dialog.component';

export class AIFPlugin extends CKEditor.Plugin {
    private readonly _headerLabels: Record<string, string> = {
        no: '#',
        shortFieldLabel: 'Short Label',
        description: 'Description',
    };

    private _buildTableHTML(result: any): string {
        if (!result || !Array.isArray(result.fields) || result.fields.length === 0) return '';

        const headers = ['no', 'shortFieldLabel', 'description'];

        const rows = result.fields.map(row => {
            return `<tr>${headers.map(h => `<td>${this._escape(row[h])}</td>`).join('')}</tr>`;
        });

        const thead = `<thead><tr>${headers.map(h => `<th>${this._escape(this._headerLabels[h] ?? h)}</th>`).join('')}</tr></thead>`;
        const tbody = `<tbody>${rows.join('')}</tbody>`;

        return `
            <div class="aif-wrapper">
                <h4>${this._escape(result.itemName)}</h4>
                <table>${thead}${tbody}</table>
            </div>
        `;
    }

    private _escape(value: any): string {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    init() {
        const editor = this.editor;

        editor.ui.componentFactory.add('aifButton', () => {
            const button = new CKEditor.ButtonView();

            button.set({
                label: '🙈',
                withText: true,
            });

            button.on('execute', () => {
                editor.model.change(async writer => {
                    const callback = editor.config.get('aifButtonClick');
                    if (typeof callback === 'function') {
                        const dialogRef: MatDialogRef<AifDialogComponent, any> = callback();

                        lastValueFrom(dialogRef.beforeClosed()).then(result => {
                            if (!result) {
                                return;
                            }

                            editor.model.change(writer => {
                                const insertPosition =
                                    editor.model.document.selection.getFirstPosition();
                                const aiqElement = writer.createElement('aif', {
                                    class: 'styled-aif',
                                });

                                const html = this._buildTableHTML(result);

                                const viewFragment = editor.data.processor.toView(html);
                                const modelFragment = editor.data.toModel(viewFragment);

                                writer.insert(modelFragment, aiqElement);
                                editor.model.insertContent(aiqElement, insertPosition);
                                writer.setSelection(aiqElement, 'end');
                            });
                        });
                    }
                });
            });

            return button;
        });

        // Define schema for AIQ tag
        editor.model.schema.register('aif', {
            allowWhere: '$text',
            isInline: true,
            allowContentOf: '$block',
            allowAttributes: ['class'],
        });

        // View-to-model conversion
        editor.conversion.for('upcast').elementToElement({
            view: {
                name: 'aif',
            },
            model: (viewElement, { writer }) => {
                return writer.createElement('aif', {
                    class: viewElement.getAttribute('class') || 'styled-aif',
                });
            },
        });

        // Model-to-view conversion
        editor.conversion.for('downcast').elementToElement({
            model: 'aif',
            view: (modelElement, { writer }) => {
                return writer.createContainerElement('aif', {
                    class: 'styled-aif',
                });
            },
        });

        // ✅ Handle Enter Key: Close AIQ and start a new paragraph
        editor.keystrokes.set('Enter', (event, cancel) => {
            const selection = editor.model.document.selection;
            const position = selection.getFirstPosition();

            if (position && position.parent.name === 'aif') {
                event.preventDefault(); // Prevent default Enter behavior
                cancel();

                editor.model.change(writer => {
                    const aiqElement: any = position.parent;
                    const parentContainer = aiqElement.parent;

                    // Move cursor after AIQ element
                    const newPosition = writer.createPositionAfter(aiqElement);

                    // Insert a new paragraph
                    const newParagraph = writer.createElement('paragraph');
                    writer.insert(newParagraph, newPosition);
                    writer.setSelection(newParagraph, 'in');

                    // Exit AIQ tag
                    editor.editing.view.focus();
                });
            }
        });
    }
}
